import clsx from 'clsx';
import React, { useMemo, useReducer, useState } from 'react';
import { useIntl } from 'react-intl';
import { useQuery } from 'react-query';
import { useParams } from 'react-router-dom';
import Select from 'react-select';
import CreatableSelect from 'react-select/creatable';
import { Column } from 'react-table';
import { toTitleCase } from '../../../../../../_metronic/helpers';
import { api } from '../../../../../modules/api/api';
import { useAuth } from '../../../../../modules/auth';
import Avatar from '../../../../../modules/components/avatar/Avatar';
import Datatable from '../../../../../modules/components/datatable/Datatable';
import Header from '../../../../../modules/components/datatable/Header';
import UnlabeledBulletColumns from '../../../../../modules/components/list/UnlabeledBulletColumns';
import ListLoading from '../../../../../modules/components/loading/ListLoading';
import { useToast } from '../../../../../modules/components/toast/ToastContext';
import { isoCountryMap } from '../../../../../modules/helpers/isoCountryMap';
import { DataSiftField, SiService, siteIntelligenceServiceLabel } from '../../../mappings';
import EditableCardContainer from '../card/EditableCardContainer';
import { AddWebContent } from '../form/AddWebContent';
import { ServiceSelector, UsePageDataManagerResult } from '../hooks/usePageDataManager';
import { HistoryModal } from '../modal/HistoryModal';
import { getFieldUpdate, getTranslatedValueWithFallback, SiRequestUser } from '../utils';

interface Sort {
  name: string;
  sort: 1 | -1;
}

interface Props {
  categoryData: any;
  inboundData: any;
  translation: { key: string, value: string, language: 'sc' }[];
  currentLanguage: 'sc' | 'en' | 'tc';
  refetch: () => void;
}

export default function TrafficSearchDetail({
  categoryData,
  inboundData,
  translation,
  currentLanguage,
  refetch,
}: Props) {
  const { currentUser, auth, refresh } = useAuth();
  const intl = useIntl();
  const t = (id: string) => intl.formatMessage({ id: `SI_TRAFFIC_SEARCH_DETAIL.${id}` });
  const { setToast } = useToast();
  const emDash = '—';

  const categoryResults = categoryData;
  const inboundResults = inboundData;

  const seo = categoryResults?.result?.seo ?? {};
  const traffic = inboundResults?.result?.traffic ?? {};

  const toDisplay = (value: unknown) => {
    if (value === null || value === undefined) {
      return emDash;
    }

    if (typeof value === 'string') {
      const trimmed = value.trim();

      if (trimmed.length === 0) {
        return emDash;
      }

      return trimmed.charAt(0).toUpperCase() + trimmed.slice(1);
    }

    return String(value);
  };

  const monthlySessions = toDisplay(getTranslatedValueWithFallback(
    translation,
    currentLanguage,
    'traffic.visit_monthly_session',
    traffic?.visit_monthly_session
  ));


  const averageSessionDuration = toDisplay(getTranslatedValueWithFallback(
    translation,
    currentLanguage,
    'traffic.avarage_session_duration',
    traffic?.avarage_session_duration
  ));
  const hasTopAudience = traffic?.most_audience_country
  const topAudience = toDisplay(getTranslatedValueWithFallback(
    translation,
    currentLanguage,
    'traffic.most_audience_country',
    traffic?.most_audience_country
  ));

  const seoScore = toDisplay(getTranslatedValueWithFallback(
    translation,
    currentLanguage,
    'seo.score',
    seo?.score
  ));
  const backlinks = toDisplay(getTranslatedValueWithFallback(
    translation,
    currentLanguage,
    'seo.backlinks',
    seo?.backlinks
  ));
  const googlePenalty = toDisplay(getTranslatedValueWithFallback(
    translation,
    currentLanguage,
    'seo.google_penalty_detected',
    seo?.google_penalty_detected
  ));

  return (
    <>
      <EditableCardContainer
        title={t('TITLE')}
      >
        <div className='p-9 vstack gap-9'>
          <div className='row g-5'>
            <div className='col-12 col-lg-6'>
              <div className='border border-gray-300 rounded-4 p-5 h-100 bg-light'>
                <div className='d-flex align-items-center gap-3 text-uppercase fw-bolder fs-3 text-gray-500 mb-4 ls-2'>
                  <i className='bi bi-people-fill text-primary fs-3' />
                  <span>{t('TRAFFIC_METRICS')}</span>
                </div>

                <div className='separator separator-dashed border-gray-300 mb-4' />

                <div className='vstack gap-3'>
                  <div className='d-flex align-items-center justify-content-between gap-4'>
                    <span className='text-gray-700 fs-6'>{t('MONTHLY_SESSIONS')}:</span>
                    <span className='text-gray-900 fw-bold fs-6'>{monthlySessions}</span>
                  </div>

                  <div className='d-flex align-items-center justify-content-between gap-4'>
                    <span className='text-gray-700 fs-6'>{t('AVG_SESSION_DURATION')}:</span>
                    <span className='text-gray-900 fw-bold fs-6'>{averageSessionDuration}</span>
                  </div>

                  <div className='d-flex align-items-center justify-content-between gap-4'>
                    <span className='text-gray-700 fs-6'>{t('TOP_AUDIENCE')}:</span>
                    <span className='text-gray-900 fw-bold fs-6 d-inline-flex align-items-center gap-2'>
                      {hasTopAudience && <i className='bi bi-flag' />}
                      <span>{topAudience}</span>
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div className='col-12 col-lg-6'>
              <div className='border border-gray-300 rounded-4 p-5 h-100 bg-light'>
                <div className='d-flex align-items-center gap-3 text-uppercase fw-bolder fs-3 text-gray-500 mb-4 ls-2'>
                  <i className='bi bi-search text-primary fs-3' />
                  <span>{t('SEO_AND_DOMAIN_AUTHORITY')}</span>
                </div>

                <div className='separator separator-dashed border-gray-300 mb-4' />

                <div className='vstack gap-3'>
                  <div className='d-flex align-items-center justify-content-between gap-4'>
                    <span className='text-gray-700 fs-6'>{t('SEO_SCORE')}:</span>
                    <span className='text-gray-900 fw-bold fs-6'>{seoScore}</span>
                  </div>

                  <div className='d-flex align-items-center justify-content-between gap-4'>
                    <span className='text-gray-700 fs-6'>{t('BACKLINKS')}:</span>
                    <span className='text-gray-900 fw-bold fs-6'>{backlinks}</span>
                  </div>

                  <div className='d-flex align-items-center justify-content-between gap-4'>
                    <span className='text-gray-700 fs-6'>{t('GOOGLE_PENALTY')}:</span>
                    <span className='text-gray-900 fw-bold fs-6'>{googlePenalty}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className='d-flex justify-content-end mb-5'>
          <a href='#summary' className='px-8 d-none print-display-button'>
            <i className="bi bi-caret-up-square-fill fs-3"></i>
          </a>
        </div>
      </EditableCardContainer>
    </>
  );
}
